import { useState, useEffect, Dispatch, SetStateAction, useCallback } from "react";
import { LocalStorageKeys } from "../constants/local_storage";

// Shared state to track all active listeners
const listeners = new Map<LocalStorageKeys, Set<() => void>>();

function getStorageValue(key: LocalStorageKeys) {
  return localStorage.getItem(key);
}

/** Notify all listeners for a specific key */
function notifyListeners(key: LocalStorageKeys) {
  const keyListeners = listeners.get(key);
  if (keyListeners) {
    keyListeners.forEach(listener => listener());
  }
}

/** Custom event listener for cross-tab synchronization */
function handleStorageEvent(event: StorageEvent) {
  if (event.key && Object.values(LocalStorageKeys).includes(event.key as LocalStorageKeys)) {
    notifyListeners(event.key as LocalStorageKeys);
  }
}

/** Set up global storage event listener */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', handleStorageEvent);
}

/**
 * A hook to manage local storage with cross-component synchronization.
 * @see https://react-native-async-storage.github.io/async-storage/docs/api#useasyncstorage to implement in React Native.
 * @param key - The key to store the value.
 * @returns A tuple with the value and the setter function.
 */
export const useLocalStorage = (key: LocalStorageKeys): [string | null, Dispatch<SetStateAction<string | null>>] => {
  const [value, setValue] = useState(() => {
    return getStorageValue(key);
  });

  // Create a callback to update the component's state
  const updateValue = useCallback(() => {
    setValue(getStorageValue(key));
  }, [key]);

  useEffect(() => {
    // Register this component as a listener for this key
    if (!listeners.has(key)) {
      listeners.set(key, new Set());
    }
    listeners.get(key)!.add(updateValue);

    // Cleanup: remove listener when component unmounts
    return () => {
      const keyListeners = listeners.get(key);
      if (keyListeners) {
        keyListeners.delete(updateValue);
        if (keyListeners.size === 0) {
          listeners.delete(key);
        }
      }
    };
  }, [key, updateValue]);

  // Custom setter that updates localStorage and notifies all listeners
  const setLocalStorageValue = useCallback<Dispatch<SetStateAction<string | null>>>((newValue) => {
    const resolvedValue = typeof newValue === 'function' ? newValue(value) : newValue;
    
    // Update localStorage
    if (resolvedValue) {
      localStorage.setItem(key, resolvedValue);
    } else {
      localStorage.removeItem(key);
    }
    
    // Update local state
    setValue(resolvedValue);
    
    // Notify all other components using the same key
    notifyListeners(key);
  }, [key, value]);

  return [value, setLocalStorageValue];
};